import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { EmojiBar, EmojiPicker } from '../../chat/EmojiPicker'
import { PhoneSheet } from '../../chats/phone/PhoneSheet'
import { FeatherIcon } from '../../components/FeatherIcon'
import { Spinner } from '../../components/Spinner'
import { useMediaSrc } from '../../library/useMediaSrc'
import { formatDuration, formatUsd, type Playable } from '../data'
import { useFanslyApi } from '../deps'
import { uploadToFansly } from '../upload'
import { toFanslyError } from '../useFanslyQuery'
import { VaultPicker } from '../ui/VaultPicker'
import { GifPicker } from './GifPicker'
import type { ChatGif } from './model'
import { PRICE_MAX, PRICE_MIN, readPrice, sendRequestFor } from './send'
import type { SendRequest, Thread } from './useThread'
import { useVoiceRecorder, type RecordFailure, type VoiceNote } from './useVoiceRecorder'

/** A typing ping goes at most this often while the operator types. */
export const TYPING_EVERY_MS = 4000

export const WRITE_LANGS = ['en', 'ru', 'uk', 'fr', 'de', 'es', 'it', 'pt', 'pl', 'tr'] as const
const LANG_KEY = 'om.fsc.writeLang'

function readLang(): string {
  try {
    const v = localStorage.getItem(LANG_KEY)
    return v && (WRITE_LANGS as readonly string[]).includes(v) ? v : 'en'
  } catch {
    return 'en'
  }
}

function saveLang(v: string) {
  try {
    localStorage.setItem(LANG_KEY, v)
  } catch {
    /* storage unavailable */
  }
}

/** A file on its way into the message: from the vault, or uploaded from this device. */
interface Att {
  key: string
  /** The Fansly media id once known (vault files have it at once). */
  mediaId: string
  media: Playable
  /** 0..1 while uploading. */
  progress?: number
  error?: string
}

function AttChip({ a, onRemove }: { a: Att; onRemove: () => void }) {
  const { t } = useTranslation('fanslyChats')
  const url = a.media.poster || (a.media.kind === 'image' ? a.media.src : '')
  const local = /^(blob|data):/.test(url)
  const remote = useMediaSrc(local ? '' : url)
  const src = local ? url : remote.src
  return (
    <span ref={remote.ref as React.RefObject<HTMLSpanElement>} className={`rc-attach ${a.error ? 'is-error' : ''}`} title={a.error || t(`kind.${a.media.kind}`)}>
      {src ? <img src={src} alt="" /> : <FeatherIcon name={a.media.kind === 'video' ? 'film' : a.media.kind === 'audio' ? 'music' : 'image'} size={16} />}
      {a.progress != null && (
        <span className="rc-attach-state rc-attach-state--busy">
          <Spinner size={14} label={t('composer.uploading')} />
        </span>
      )}
      {a.error && <span className="rc-attach-state">{t('composer.failed')}</span>}
      {a.media.kind === 'video' && !a.error && a.progress == null && (
        <span className="rc-attach-state">
          <FeatherIcon name="play" size={9} />
        </span>
      )}
      <button type="button" className="rc-attach-x" onClick={onRemove} title={t('composer.remove')} aria-label={t('composer.remove')}>
        <FeatherIcon name="x" size={12} />
      </button>
    </span>
  )
}

/** The recorded voice note before it goes: listen back, or remove it. */
function VoiceChip({ note, onRemove }: { note: VoiceNote; onRemove: () => void }) {
  const { t } = useTranslation('fanslyChats')
  const [playing, setPlaying] = useState(false)
  return (
    <span className="rc-attach rc-attach--voice" title={t('voice.note')}>
      <button type="button" className="rc-attach-play" onClick={() => setPlaying((v) => !v)} title={playing ? t('voice.pause') : t('voice.play')} aria-label={playing ? t('voice.pause') : t('voice.play')}>
        <FeatherIcon name={playing ? 'pause' : 'play'} size={13} />
      </button>
      {playing && <audio src={note.url} autoPlay onEnded={() => setPlaying(false)} />}
      <span className="rc-attach-voice-len">{formatDuration(note.seconds)}</span>
      <button type="button" className="rc-attach-x" onClick={onRemove} title={t('composer.remove')} aria-label={t('composer.remove')}>
        <FeatherIcon name="x" size={12} />
      </button>
    </span>
  )
}

interface Props {
  groupId: string
  fanId: string
  fanName: string
  thread: Thread
  phone: boolean
  /** Text put into the draft from outside (a quoted message), `n` makes a repeat count. */
  insert?: { text: string; n: number } | null
}

/**
 * The open chat's composer: text (Enter sends), media from the vault or this
 * device, a PPV price (one item = paid media, several = a paid bundle), a
 * voice note, GIFs, emoji and draft translation. Sends are optimistic: the
 * draft clears at once and comes back if the operator cancels the
 * prohibited-wording warning.
 */
export function Composer({ groupId, fanId, fanName, thread, phone, insert }: Props) {
  const { t, i18n } = useTranslation('fanslyChats')
  const api = useFanslyApi()
  const [text, setText] = useState('')
  const [atts, setAtts] = useState<Att[]>([])
  const [voice, setVoice] = useState<VoiceNote | null>(null)
  const [price, setPrice] = useState('')
  const [priceOpen, setPriceOpen] = useState(false)
  const [panel, setPanel] = useState<'gif' | 'emoji' | null>(null)
  const [emojiBar, setEmojiBar] = useState(false)
  const [emojiRev, setEmojiRev] = useState(0)
  const [vault, setVault] = useState(false)
  const [toolsOpen, setToolsOpen] = useState(false)
  const [notice, setNotice] = useState('')
  const [lang, setLang] = useState(readLang)
  const [undo, setUndo] = useState<string | null>(null)
  const [translating, setTranslating] = useState(false)
  const field = useRef<HTMLTextAreaElement>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const lastTyping = useRef(0)
  const urls = useRef<string[]>([])
  const uploads = useRef(new Map<string, AbortController>())

  useEffect(
    () => () => {
      uploads.current.forEach((c) => c.abort())
      urls.current.forEach((u) => URL.revokeObjectURL(u))
    },
    [],
  )

  useEffect(() => {
    if (!insert?.text) return
    setText((prev) => (prev.trim() ? `${prev.trimEnd()}\n${insert.text}` : insert.text))
    field.current?.focus()
  }, [insert])

  const recorder = useVoiceRecorder((note, failure?: RecordFailure) => {
    if (note) {
      urls.current.push(note.url)
      setVoice(note)
    } else if (failure) setNotice(t(`voice.${failure}`))
  })

  const hasMedia = atts.length > 0 || !!voice
  const { value: priceValue, problem } = readPrice(price, hasMedia)
  const paid = priceValue >= PRICE_MIN && !problem
  const uploading = atts.some((a) => a.progress != null)
  const broken = atts.some((a) => a.error)
  const canSend = !recorder.recording && !uploading && !broken && !problem && (!!text.trim() || hasMedia)

  const typing = () => {
    const now = Date.now()
    if (now - lastTyping.current < TYPING_EVERY_MS) return
    lastTyping.current = now
    void api.post('/crm/messages/typing', { groupId, _crm_fan_id: fanId }).catch(() => undefined)
  }

  const addFiles = (files: File[]) => {
    const usable = files.filter((f) => /^(image|video)\//.test(f.type))
    if (!usable.length) return
    setVoice(null)
    for (const file of usable) {
      const key = `u-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
      const url = URL.createObjectURL(file)
      urls.current.push(url)
      const kind = file.type.startsWith('video/') ? 'video' : 'image'
      const media: Playable = { mediaId: '', kind, src: url, poster: kind === 'image' ? url : '', width: 0, height: 0, duration: 0 }
      setAtts((prev) => [...prev, { key, mediaId: '', media, progress: 0 }])
      const ctl = new AbortController()
      uploads.current.set(key, ctl)
      uploadToFansly(api, file, { signal: ctl.signal, onProgress: (p) => setAtts((prev) => prev.map((a) => (a.key === key ? { ...a, progress: p.fraction } : a))) })
        .then((mediaId) => setAtts((prev) => prev.map((a) => (a.key === key ? { ...a, mediaId, progress: undefined, media: { ...a.media, mediaId } } : a))))
        .catch((e: unknown) => {
          if (e instanceof DOMException && e.name === 'AbortError') return
          setAtts((prev) => prev.map((a) => (a.key === key ? { ...a, progress: undefined, error: toFanslyError(e).message } : a)))
        })
        .finally(() => uploads.current.delete(key))
    }
  }

  const removeAtt = (key: string) => {
    uploads.current.get(key)?.abort()
    setAtts((prev) => prev.filter((a) => a.key !== key))
  }

  const submit = () => {
    if (!canSend) return
    const content = text.trim()
    const sentPrice = paid ? priceValue : 0
    const saved = { text, atts, voice, price, priceOpen }
    let prepare: () => Promise<SendRequest>
    let media: Playable[] = []
    if (voice) {
      const note = voice
      let mediaId = ''
      prepare = async () => {
        // Uploaded once: a retry sends the same file again.
        if (!mediaId) mediaId = await uploadToFansly(api, note.file)
        return sendRequestFor({ groupId, fanId, content, mediaIds: [], price: sentPrice, voiceMediaId: mediaId })
      }
    } else {
      const ids = atts.map((a) => a.mediaId)
      media = atts.map((a) => a.media)
      const req = sendRequestFor({ groupId, fanId, content, mediaIds: ids, price: sentPrice })
      prepare = async () => req
    }
    setText('')
    setAtts([])
    setVoice(null)
    setPrice('')
    setPriceOpen(false)
    setUndo(null)
    setPanel(null)
    field.current?.focus()
    void thread.send({ content, media, gif: null, voice: !!voice, price: sentPrice, prepare }).then((outcome) => {
      if (outcome !== 'cancelled') return
      // The operator kept the wording back: the draft returns as it was.
      setText((cur) => (cur ? cur : saved.text))
      setAtts((cur) => (cur.length ? cur : saved.atts))
      setVoice((cur) => cur ?? saved.voice)
      setPrice((cur) => cur || saved.price)
      setPriceOpen((cur) => cur || saved.priceOpen)
    })
  }

  const sendGif = (gif: ChatGif) => {
    setPanel(null)
    const req = sendRequestFor({ groupId, fanId, content: gif.url, mediaIds: [], price: 0 })
    void thread.send({ content: '', media: [], gif, voice: false, price: 0, prepare: async () => req })
  }

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing || e.keyCode === 229) return
    if (phone) return
    e.preventDefault()
    submit()
  }

  const translateDraft = async () => {
    if (undo != null) {
      setText(undo)
      setUndo(null)
      return
    }
    const draft = text.trim()
    if (!draft) {
      setNotice(t('composer.translateEmpty'))
      return
    }
    setTranslating(true)
    try {
      const r = await api.translate(draft, lang)
      if (!r.same && r.translated) {
        setUndo(text)
        setText(r.translated)
      } else setNotice(t('composer.translateSame'))
    } catch (e) {
      setNotice(toFanslyError(e).message)
    } finally {
      setTranslating(false)
    }
  }

  const insertText = (s: string) => {
    const el = field.current
    const at = el?.selectionStart ?? text.length
    const end = el?.selectionEnd ?? at
    setText(text.slice(0, at) + s + text.slice(end))
    window.requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(at + s.length, at + s.length)
    })
  }

  const startVoice = () => {
    if (atts.length) {
      setNotice(t('voice.removeMedia'))
      return
    }
    setPanel(null)
    void recorder.start()
  }

  const sendLabel = paid ? t('composer.sendPaid', { price: formatUsd(priceValue, i18n.language) }) : t('composer.send')
  const itemsCount = voice ? 1 : atts.length

  const tools: Array<{ key: string; icon?: string; label?: string; title: string; on?: boolean; disabled?: boolean; run: () => void }> = [
    { key: 'vault', icon: 'archive', title: t('composer.vault'), disabled: !!voice, run: () => setVault(true) },
    { key: 'upload', icon: 'image', title: t('composer.upload'), disabled: !!voice, run: () => fileInput.current?.click() },
    { key: 'price', icon: 'tag', title: t('composer.price'), on: priceOpen || paid, run: () => setPriceOpen((v) => !v) },
    { key: 'voice', icon: 'mic', title: t('composer.voice'), on: recorder.recording, disabled: recorder.recording, run: startVoice },
    { key: 'gif', label: 'GIF', title: t('composer.gif'), on: panel === 'gif', run: () => setPanel((p) => (p === 'gif' ? null : 'gif')) },
    { key: 'emoji', icon: 'smile', title: t('composer.emoji'), on: phone ? panel === 'emoji' : emojiBar, run: () => (phone ? setPanel((p) => (p === 'emoji' ? null : 'emoji')) : setEmojiBar((v) => !v)) },
  ]

  const field_ = (
    <div className="rc-field-wrap">
      <textarea
        ref={field}
        value={text}
        rows={phone ? 1 : 2}
        placeholder={t('composer.placeholder', { name: fanName })}
        aria-label={t('composer.label')}
        onChange={(e) => {
          setText(e.target.value)
          if (undo != null) setUndo(null)
          if (e.target.value.trim()) typing()
        }}
        onKeyDown={onKey}
        onPaste={(e) => {
          const files = Array.from(e.clipboardData?.files || []).filter((f) => /^(image|video)\//.test(f.type))
          if (files.length) {
            e.preventDefault()
            addFiles(files)
          }
        }}
      />
    </div>
  )

  return (
    <div className={`rc-composer-wrap fsc-composer ${phone ? 'is-phone' : ''}`}>
      {panel === 'gif' && <GifPicker onPick={sendGif} onClose={() => setPanel(null)} />}
      {panel === 'emoji' && <EmojiPicker onPick={insertText} onClose={() => setPanel(null)} onShortcutsChange={() => setEmojiRev((n) => n + 1)} />}
      {!phone && emojiBar && <EmojiBar key={emojiRev} onPick={insertText} onOpenAll={() => setPanel('emoji')} />}
      {notice && (
        <div className="rc-notice" role="status">
          {notice}
          <button type="button" className="rc-notice-x" onClick={() => setNotice('')} title={t('close')} aria-label={t('close')}>
            <FeatherIcon name="x" size={12} />
          </button>
        </div>
      )}
      {recorder.recording && (
        <div className="rc-rec" role="status">
          <span className="rc-rec-dot" />
          <span className="rc-rec-label">{t('voice.recording')}</span>
          <span className="rc-rec-time">{formatDuration(recorder.seconds)}</span>
          <span style={{ flex: 1 }} />
          <button type="button" className="rc-btn" onClick={recorder.cancel}>
            {t('cancel')}
          </button>
          <button type="button" className="rc-btn rc-btn--primary" onClick={recorder.stop}>
            <FeatherIcon name="square" size={12} />
            {t('voice.stop')}
          </button>
        </div>
      )}
      <div className={`rc-composer ${paid ? 'is-paid' : ''}`}>
        {paid && (
          <div className="rc-paid-head">
            <FeatherIcon name="lock" size={13} />
            <span>{itemsCount > 1 ? t('composer.ppvBundle', { price: formatUsd(priceValue, i18n.language), count: itemsCount }) : t('composer.ppvSingle', { price: formatUsd(priceValue, i18n.language) })}</span>
          </div>
        )}
        {(atts.length > 0 || voice) && (
          <div className="rc-attachments">
            {voice && (
              <VoiceChip note={voice} onRemove={() => setVoice(null)} />
            )}
            {atts.map((a) => (
              <AttChip key={a.key} a={a} onRemove={() => removeAtt(a.key)} />
            ))}
          </div>
        )}
        {!phone && field_}
        {(priceOpen || paid || problem) && (
          <div className="rc-price-row">
            <label className="rc-price">
              <FeatherIcon name="lock" size={13} />
              <span>{t('composer.ppvPrice')}</span>
              <span className="rc-price-input">
                $
                <input
                  inputMode="decimal"
                  value={price}
                  placeholder={t('composer.free')}
                  aria-label={t('composer.ppvPrice')}
                  aria-invalid={!!problem}
                  autoFocus={priceOpen && !price}
                  onChange={(e) => setPrice(e.target.value.replace(/[^\d.,]/g, ''))}
                />
              </span>
              <button type="button" className="rc-price-x" title={t('composer.clearPrice')} aria-label={t('composer.clearPrice')} onClick={() => (setPrice(''), setPriceOpen(false))}>
                <FeatherIcon name="x" size={12} />
              </button>
            </label>
            <span className={problem ? 'rc-error' : 'rc-muted'}>{problem ? t(`composer.price_${problem}`, { min: PRICE_MIN, max: PRICE_MAX }) : t('composer.priceHint', { min: PRICE_MIN, max: PRICE_MAX })}</span>
          </div>
        )}
        <input ref={fileInput} type="file" accept="image/*,video/*" multiple hidden onChange={(e) => (addFiles(Array.from(e.target.files || [])), (e.target.value = ''))} />
        {phone ? (
          <div className="rc-prow">
            <button type="button" className={`rc-prow-plus ${toolsOpen ? 'is-on' : ''}`} aria-label={t('composer.tools')} aria-haspopup="dialog" onClick={() => setToolsOpen(true)}>
              <FeatherIcon name="plus" size={22} />
            </button>
            {field_}
            {canSend || text.trim() || hasMedia ? (
              <button type="button" className={`rc-prow-send ${paid ? 'is-paid' : ''}`} disabled={!canSend} aria-label={sendLabel} onClick={submit}>
                {paid && <span>{formatUsd(priceValue, i18n.language)}</span>}
                <FeatherIcon name="arrow-up" size={20} />
              </button>
            ) : (
              <button type="button" className="rc-prow-mic" aria-label={t('composer.voice')} disabled={recorder.recording} onClick={startVoice}>
                <FeatherIcon name="mic" size={20} />
              </button>
            )}
          </div>
        ) : (
          <div className="rc-tools">
            <div className="rc-tool-row">
              {tools.map((tool) => (
                <button
                  key={tool.key}
                  type="button"
                  className={`rc-tool ${tool.label ? 'rc-tool--gif' : ''} ${tool.on ? 'is-on' : ''}`}
                  data-tip={tool.title}
                  aria-label={tool.title}
                  title={tool.title}
                  disabled={tool.disabled}
                  onClick={tool.run}
                >
                  {tool.icon ? <FeatherIcon name={tool.icon} size={18} /> : tool.label}
                </button>
              ))}
            </div>
            <select className="fsc-lang" value={lang} aria-label={t('composer.translateTo')} title={t('composer.translateTo')} onChange={(e) => (setLang(e.target.value), saveLang(e.target.value))}>
              {WRITE_LANGS.map((l) => (
                <option key={l} value={l}>
                  {l.toUpperCase()}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={`rc-translate ${undo != null ? 'is-undo' : ''}`}
              aria-label={undo != null ? t('composer.undoTranslate') : t('composer.translate', { lang: lang.toUpperCase() })}
              title={undo != null ? t('composer.undoTranslate') : t('composer.translate', { lang: lang.toUpperCase() })}
              disabled={translating || (!text.trim() && undo == null)}
              onClick={() => void translateDraft()}
            >
              {translating ? <Spinner size={12} label={t('composer.translating')} /> : <FeatherIcon name={undo != null ? 'rotate-ccw' : 'globe'} size={13} />}
              <span className="rc-translate-label">{undo != null ? t('composer.undoTranslate') : `→ ${lang.toUpperCase()}`}</span>
            </button>
            <button type="button" className="rc-send" disabled={!canSend} onClick={submit} title={t('composer.enterToSend')}>
              <span className="rc-send-label">{sendLabel}</span>
              <FeatherIcon name="arrow-up" size={14} />
            </button>
          </div>
        )}
      </div>
      {!phone && <div className="rc-hint">{t('composer.hint')}</div>}
      {phone && (
        <PhoneSheet open={toolsOpen} onClose={() => setToolsOpen(false)} label={t('composer.tools')}>
          <div className="rc-tool-grid">
            {[
              ...tools,
              { key: 'translate', icon: undo != null ? 'rotate-ccw' : 'globe', title: undo != null ? t('composer.undoTranslate') : t('composer.translate', { lang: lang.toUpperCase() }), disabled: translating || (!text.trim() && undo == null), run: () => void translateDraft() },
            ].map((tool) => (
              <button
                key={tool.key}
                type="button"
                className={`rc-tool-tile ${'on' in tool && tool.on ? 'is-on' : ''}`}
                disabled={tool.disabled}
                onClick={() => {
                  setToolsOpen(false)
                  tool.run()
                }}
              >
                <span className="rc-tool-tile-ic">{tool.icon ? <FeatherIcon name={tool.icon} size={25} /> : <b>{'label' in tool ? tool.label : ''}</b>}</span>
                <span className="rc-tool-tile-label">{tool.title}</span>
              </button>
            ))}
          </div>
        </PhoneSheet>
      )}
      {vault && (
        <VaultPicker
          title={t('composer.vaultTitle')}
          confirmLabel={t('composer.attach')}
          onClose={() => setVault(false)}
          onPick={(picked) => {
            setVault(false)
            setVoice(null)
            setAtts((prev) => {
              const have = new Set(prev.map((a) => a.mediaId))
              return [...prev, ...picked.filter((p) => !have.has(p.mediaId)).map((p) => ({ key: `v-${p.mediaId}`, mediaId: p.mediaId, media: p.media }))]
            })
            field.current?.focus()
          }}
        />
      )}
    </div>
  )
}
